import { useMutation, useQuery } from '@tanstack/react-query'
import { createDdmApi, type PlayRow } from '@bloo-team/api-client'
import { buildDql, client, isDemoSession, runToCompletion, searchApiFor } from '@bloo-team/core-shell'
import { DEMO_EXTRACTORS, extractorsApi, type Extractor } from './extractors'
import { DEMO_DDM, demoDetailFor } from './extractor-detail-demo'
import { extractorOverlayStore } from './extractor-store'
import { simulateDemo } from './extractor-yaml'

/**
 * Detail-page data (docs/extractors-requirements.md W2): the YAML and
 * samples, the simulation, the data model, and "pull recent" from search.
 * Live = captured endpoints (inventory/extractors-write-capture.md); demo =
 * the fixture YAMLs and the offline simulator, labelled on the page.
 */

export const ddmApi = createDdmApi(client)

export interface ExtractorDetailData {
  yaml: string
  samples: string[]
}

/** `enabled: false` keeps the query idle — the catalogue slide-over mounts before any row is open. */
export function useExtractorDetail({ clusterId, scopeId, extractorId, row, enabled = true }: { clusterId: string; scopeId: string; extractorId: string; row: Extractor | null; enabled?: boolean }) {
  const isDemo = isDemoSession()
  return useQuery<ExtractorDetailData>({
    queryKey: ['extractor-detail', clusterId, scopeId, extractorId, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) {
        const base = extractorId.replace(/_draft$/, '')
        // A published demo write (overlay) supersedes the fixture — what listparserdetails would now return.
        const pub = extractorOverlayStore(clusterId, scopeId).all()[base]?.published
        if (pub) return { yaml: pub.yaml, samples: pub.samples }
        const r = row ?? DEMO_EXTRACTORS.find((e) => e.extractor_id === extractorId) ?? null
        const d = demoDetailFor(extractorId, r?.name ?? extractorId, r?.source_name ?? null)
        return { yaml: d.yaml, samples: d.samples }
      }
      const d = await extractorsApi.details({ clusterId, scopeId, extractorId })
      return { yaml: d.yaml, samples: d.logevents.map((s) => s.replace(/\n$/, '')) }
    },
    // A draft answers 400 (CB-29); retrying will not change that.
    retry: false,
    staleTime: 60_000,
    enabled,
  })
}

/** The legacy editor sends the samples concatenated; each ends in a newline so lines stay separable. */
export function joinSamples(samples: string[]): string {
  return samples.map((s) => (s.endsWith('\n') ? s : `${s}\n`)).join('')
}

export interface PlayInput {
  yaml: string
  samples: string[]
  extractorId: string
}

export function usePlay({ clusterId, scopeId, yaml, samples, extractorId, enabled = true }: { clusterId: string; scopeId: string; enabled?: boolean } & PlayInput) {
  const isDemo = isDemoSession()
  return useQuery<{ rows: PlayRow[]; total: number; demo: boolean }>({
    queryKey: ['extractor-play', clusterId, scopeId, extractorId, isDemo ? 'demo' : 'live', yaml, samples],
    queryFn: async () => {
      if (isDemo) {
        const rows = simulateDemo(yaml, samples, extractorId)
        return { rows, total: rows.length, demo: true }
      }
      const r = await extractorsApi.play({ clusterId, scopeId, yaml, logevent: joinSamples(samples), pageNo: 1, pageSize: 200 })
      return { ...r, demo: false }
    },
    enabled: enabled && yaml.trim().length > 0 && samples.length > 0,
    retry: false,
    staleTime: Infinity,
    // Keep the last parse on screen while the next one runs — flicker on every keystroke reads as breakage.
    placeholderData: (prev) => prev,
  })
}

export function useDdm({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  const isDemo = isDemoSession()
  return useQuery<Record<string, string[]>>({
    queryKey: ['ddm', clusterId, scopeId, isDemo ? 'demo' : 'live'],
    queryFn: async () => {
      if (isDemo) return DEMO_DDM
      const streams = await ddmApi.list({ clusterId, scopeId })
      return Object.fromEntries(streams.map((s) => [s.stream, s.fields]))
    },
    staleTime: 300_000,
    retry: 1,
  })
}

/** `stream=* where SourceName = '<source>' | duration 24h | limit 50` — the raw lines of recent real events. */
export function recentEventsQuery(sourceName: string, limit = 50): string {
  return buildDql({
    stream: '*',
    time: { kind: 'relative', duration: '24h' },
    filters: [{ field: 'SourceName', op: '=', value: sourceName }],
    limit,
  })
}

/**
 * Pull recent real events for this source into the samples pane (Cribl-style
 * capture). Rests on the UNTESTED `source_name == $SourceName` claim
 * (sql-dialect.md §7); an empty answer is reported as such, never faked.
 */
export function usePullRecent({ clusterId, scopeId }: { clusterId: string; scopeId: string }) {
  return useMutation<string[], Error, { sourceName: string; limit?: number }>({
    mutationFn: async ({ sourceName, limit = 50 }) => {
      const api = searchApiFor(isDemoSession())
      const { page } = await runToCompletion(api, { clusterId, scopeId }, recentEventsQuery(sourceName, limit), undefined, new AbortController().signal, limit)
      const lines = page.rows.map((r) => r.LogEvent).filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
      return [...new Set(lines)]
    },
  })
}
